import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiBook, FiTarget, FiCalendar, FiArrowRight } = FiIcons;

const NavigationScreen = ({ onSelect }) => {
  const tools = [
    {
      id: 'scorecard',
      title: 'Manuscript Readiness',
      subtitle: 'Scorecard',
      description: 'Assess how prepared your book is for refinement, editing, or publication',
      icon: FiBook,
      color: 'from-blue-500 to-blue-600'
    },
    {
      id: 'publishing',
      title: 'Publishing Path',
      subtitle: 'Selector',
      description: 'Discover your ideal publishing path: Traditional, Hybrid, or Indie',
      icon: FiTarget,
      color: 'from-orange-500 to-orange-600'
    },
    {
      id: 'action-plan',
      title: '7-Day Faithful',
      subtitle: 'Action Plan',
      description: 'Create a realistic weekly plan tailored to your stage and goals',
      icon: FiCalendar,
      color: 'from-purple-500 to-purple-600'
    }
  ];

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center space-y-4 mb-12"
        >
          <h1 className="text-4xl lg:text-5xl font-serif text-gray-900">
            Write. Refine. Release.
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Choose a tool to continue your publishing journey with clarity and confidence
          </p>
          <div className="w-24 h-1 bg-primary mx-auto rounded-full"></div>
        </motion.div>

        {/* Tool Cards */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {tools.map((tool, index) => (
            <motion.div
              key={tool.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <button
                onClick={() => onSelect(tool.id)}
                className="w-full h-full group"
              >
                <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-8 h-full flex flex-col">
                  {/* Icon */}
                  <div className={`w-16 h-16 bg-gradient-to-br ${tool.color} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <SafeIcon icon={tool.icon} className="text-2xl text-white" />
                  </div>

                  {/* Content */}
                  <div className="flex-grow space-y-3">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 group-hover:text-primary transition-colors">
                        {tool.title}
                      </h3>
                      <p className="text-lg text-primary font-medium">
                        {tool.subtitle}
                      </p>
                    </div>
                    <p className="text-gray-600 leading-relaxed">
                      {tool.description}
                    </p>
                  </div>

                  {/* Arrow */}
                  <div className="flex items-center justify-end mt-6 text-primary group-hover:translate-x-1 transition-transform duration-300">
                    <SafeIcon icon={FiArrowRight} className="text-xl" />
                  </div>
                </div>
              </button>
            </motion.div>
          ))}
        </div>

        {/* Branding */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="text-center space-y-4"
        >
          <div className="bg-gradient-to-r from-primary to-blue-600 rounded-2xl p-8 text-white shadow-lg">
            <h3 className="text-xl font-semibold font-serif mb-3">
              Your Complete Publishing Companion
            </h3>
            <p className="text-blue-100 leading-relaxed max-w-2xl mx-auto">
              These tools are designed to work together, guiding you from manuscript assessment through publishing decisions to actionable weekly planning.
            </p>
          </div>
          <p className="text-sm text-gray-500">
            Created by Jeremy Bouma • GospelWise Author • CROSS + PEN
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default NavigationScreen;